/* eslint-disable react/prop-types */
"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import AppModalShell, { AppModalButton } from "../common/AppModalShell";
import EventToolsBackButton from "./EventToolsBackButton";
import { resolveUserAvatarSrc } from "../../utils/userAvatar";

const normalizeCode = (value) => String(value || "").trim().toUpperCase();

const toAvatarSrc = (user) =>
  resolveUserAvatarSrc(user, "/assets/events/default_avatar.png");

const HostRoleRequestModal = ({
  visible = false,
  onClose = () => {},
  onSubmit = null,
  onRemoveCurrent = null,
  onCancelPending = null,
  candidates = [],
  currentItems = [],
  pendingItems = [],
  title = "",
  subtitle = "",
  notice = "",
  currentTitle = "",
  pendingTitle = "",
  chooseTitle = "",
  noCandidates = "",
  submitLabel = "",
  removeCurrentLabel = "",
  removingCurrentLabel = "",
  submitting = false,
  singleSelect = true,
  maxSlots = 10,
  usedSlots = 0,
  pendingCancelingIds = [],
  onBack = null,
  embedded = false,
  showSlotsSummary = true,
  confirmationEnabled = false,
  confirmationTitle = "",
  confirmationSubtitle = "",
  confirmationBackLabel = "",
  confirmLabel = "",
  renderConfirmationContent = null,
  isConfirmationBlocked = null,
}) => {
  const { t } = useTranslation("events");

  const normalizedCandidates = React.useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const item of Array.isArray(candidates) ? candidates : []) {
      const code = normalizeCode(item?.code);
      const userId = Number(item?.user_id || item?.id || 0);
      if (!code || !userId || seen.has(userId)) continue;
      seen.add(userId);
      list.push({
        ...item,
        code,
        user_id: userId,
        source: item?.source || "participant",
      });
    }
    return list;
  }, [candidates]);

  const normalizedCurrent = React.useMemo(() => {
    const seenCodes = new Set();
    const seenUserIds = new Set();
    const list = [];
    for (const item of Array.isArray(currentItems) ? currentItems : []) {
      const code = normalizeCode(item?.code);
      const userId = Number(item?.user_id || item?.id || 0);
      if (
        !code ||
        seenCodes.has(code) ||
        (userId > 0 && seenUserIds.has(userId))
      ) continue;
      seenCodes.add(code);
      if (userId > 0) seenUserIds.add(userId);
      list.push({
        ...item,
        code,
        user_id: userId > 0 ? userId : null,
      });
    }
    return list;
  }, [currentItems]);

  const normalizedPending = React.useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const item of Array.isArray(pendingItems) ? pendingItems : []) {
      const noteId = Number(item?.notification_id || item?.request_id || 0);
      const userId = Number(item?.user_id || 0);
      const code = normalizeCode(item?.code);
      if (!noteId || !userId || !code || seen.has(noteId)) continue;
      seen.add(noteId);
      list.push({
        ...item,
        notification_id: noteId,
        user_id: userId,
        code,
      });
    }
    return list;
  }, [pendingItems]);

  const [selectedUserIds, setSelectedUserIds] = React.useState([]);
  const [selectedCurrentUserIds, setSelectedCurrentUserIds] = React.useState([]);
  const [confirmationUserId, setConfirmationUserId] = React.useState(null);

  React.useEffect(() => {
    if (!visible) return;
    setSelectedUserIds([]);
    setSelectedCurrentUserIds([]);
    setConfirmationUserId(null);
  }, [visible]);

  if (!visible) return null;

  const selectedSet = new Set(selectedUserIds);
  const selectedCurrentSet = new Set(selectedCurrentUserIds);
  const pendingUserIdSet = new Set(normalizedPending.map((item) => item.user_id));
  const pendingCancelingSet = new Set(
    (Array.isArray(pendingCancelingIds) ? pendingCancelingIds : []).map((value) => Number(value || 0))
  );
  const availableSlots = Math.max(0, Number(maxSlots || 0) - Number(usedSlots || 0));
  const confirmationCandidate = confirmationUserId
    ? normalizedCandidates.find((item) => item.user_id === confirmationUserId) || null
    : null;
  const confirmationBlocked = Boolean(
    confirmationCandidate
    && typeof isConfirmationBlocked === "function"
    && isConfirmationBlocked(confirmationCandidate)
  );

  const toggleCandidate = (userId) => {
    setSelectedUserIds((prev) => {
      const id = Number(userId || 0);
      if (!id) return prev;
      if (selectedCurrentUserIds.length > 0) return prev;
      if (pendingUserIdSet.has(id)) return prev;
      if (availableSlots <= 0 && !prev.includes(id)) return prev;
      if (singleSelect) {
        if (prev.length === 1 && prev[0] === id) return [];
        return [id];
      }
      if (prev.includes(id)) return prev.filter((value) => value !== id);
      if (prev.length >= availableSlots) return prev;
      return [...prev, id];
    });
  };

  const toggleCurrent = (userId) => {
    if (typeof onRemoveCurrent !== "function") return;
    setSelectedCurrentUserIds((prev) => {
      const id = Number(userId || 0);
      if (!id || selectedUserIds.length > 0) return prev;
      if (prev.includes(id)) return prev.filter((value) => value !== id);
      return [...prev, id];
    });
  };

  const submit = () => {
    if (submitting || !selectedUserIds.length || typeof onSubmit !== "function") return;
    if (confirmationEnabled && !confirmationCandidate) {
      const nextCandidate = normalizedCandidates.find(
        (item) => item.user_id === Number(selectedUserIds[0] || 0)
      );
      if (!nextCandidate) return;
      setConfirmationUserId(nextCandidate.user_id);
      return;
    }
    if (confirmationBlocked) return;
    onSubmit(selectedUserIds);
  };

  const removeCurrent = () => {
    if (
      submitting ||
      !selectedCurrentUserIds.length ||
      typeof onRemoveCurrent !== "function"
    ) return;
    onRemoveCurrent(selectedCurrentUserIds);
  };

  const sourceLabel = (source) => {
    if (source === "waitlist") return t("events.details.host_role_modal.source_waitlist");
    return t("events.details.host_role_modal.source_participant");
  };

  const selectionFooterContent =
    selectedCurrentUserIds.length > 0 ? (
      <AppModalButton
        onClick={removeCurrent}
        disabled={submitting}
        loading={submitting}
        variant="danger"
      >
        {submitting
          ? removingCurrentLabel || removeCurrentLabel
          : removeCurrentLabel}
      </AppModalButton>
    ) : selectedUserIds.length > 0 ? (
      <AppModalButton
        onClick={submit}
        disabled={submitting}
        loading={submitting}
        className="text-[#2563EB]"
      >
        {submitting ? t("events.details.host_role_modal.sending") : submitLabel}
      </AppModalButton>
    ) : null;

  const footerContent = confirmationCandidate ? (
    <div className="grid w-full grid-cols-1 gap-3">
      <AppModalButton
        onClick={submit}
        disabled={submitting || confirmationBlocked}
        loading={submitting}
        className="text-[#2563EB]"
      >
        {submitting
          ? t("events.details.host_role_modal.sending")
          : confirmLabel || submitLabel}
      </AppModalButton>
      <button
        type="button"
        onClick={() => setConfirmationUserId(null)}
        disabled={submitting}
        className="h-11 rounded-2xl border border-white/20 bg-white/10 px-4 text-sm font-semibold text-white transition hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {confirmationBackLabel || t("events.details.host_role_modal.back_to_selection")}
      </button>
    </div>
  ) : selectionFooterContent;

  const selectionBodyContent = (
    <>
      {notice ? (
        <div className="mb-4 rounded-[18px] border border-amber-300/25 bg-amber-300/10 px-4 py-3 text-center text-sm font-medium leading-6 text-amber-50/90">
          {notice}
        </div>
      ) : null}
      {showSlotsSummary ? (
        <p className="mb-4 text-center text-xs font-semibold leading-5 text-white/70">
          {t("events.details.host_role_modal.slots_summary", {
            used: Number(usedSlots || 0),
            max: Number(maxSlots || 0),
          })}
        </p>
      ) : null}

      <section>
        {normalizedCurrent.length > 0 ? (
          <>
            <div className="text-sm font-semibold text-white/90">{currentTitle}</div>
            <div className="mt-2 overflow-x-auto overflow-y-visible">
              <div className="flex flex-nowrap gap-3 min-w-max px-2 pt-2 pb-2">
                {normalizedCurrent.map((u) => {
                  const isSelected = u.user_id && selectedCurrentSet.has(u.user_id);
                  const canSelect = typeof onRemoveCurrent === "function" && Boolean(u.user_id);
                  const isDisabled = submitting || selectedUserIds.length > 0 || !canSelect;
                  return (
                    <button
                      key={`current-${u.code}`}
                      type="button"
                      onClick={() => toggleCurrent(u.user_id)}
                      className={`flex-shrink-0 text-center ${
                        canSelect && isDisabled
                          ? "cursor-not-allowed opacity-50"
                          : ""
                      }`}
                      aria-label={`host-role-current-${u.code}`}
                      aria-pressed={canSelect ? Boolean(isSelected) : undefined}
                      disabled={isDisabled}
                    >
                      <img
                        src={toAvatarSrc(u)}
                        alt={u.code}
                        className={`w-[56px] h-[56px] rounded-full object-cover ring-2 ${
                          isSelected ? "ring-[#FF7D8C]" : "ring-white/80"
                        }`}
                      />
                      <div className="mt-1 text-base font-extrabold leading-none">{u.code}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        ) : null}
      </section>

      {normalizedPending.length > 0 ? (
        <section className="mt-4">
          <div className="text-sm font-semibold text-white/90">{pendingTitle}</div>
          <div className="mt-2 overflow-x-auto overflow-y-visible">
            <div className="flex flex-nowrap gap-3 min-w-max px-2 pt-2 pb-2">
              {normalizedPending.map((u) => {
                const canceling = pendingCancelingSet.has(u.notification_id);
                return (
                  <div
                    key={`pending-${u.notification_id}`}
                    className="flex-shrink-0 w-[132px] flex flex-col items-center"
                  >
                    <div className="grid w-[56px] justify-items-center">
                      <img
                        src={toAvatarSrc(u)}
                        alt={u.code}
                        className="block w-[56px] h-[56px] rounded-full object-cover ring-2 ring-amber-300/90"
                      />
                      <div className="mt-1 text-base font-extrabold leading-none text-center">
                        {u.code}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => onCancelPending?.(u.notification_id)}
                      disabled={submitting || canceling || typeof onCancelPending !== "function"}
                      className="mt-2 inline-flex self-center rounded-lg bg-white/15 px-3 py-1 text-[11px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {canceling
                        ? t("events.details.host_role_modal.cancelling")
                        : t("events.details.host_role_modal.cancel_request")}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ) : null}

      <section className="mt-4">
        <div className="text-sm font-semibold text-white/90">{chooseTitle}</div>
        {normalizedCandidates.length === 0 ? (
          <div className="mt-2 text-sm text-white/75">{noCandidates}</div>
        ) : (
          <div className="mt-2 overflow-x-auto overflow-y-visible">
            <div className="flex flex-nowrap gap-3 min-w-max px-2 pt-2 pb-2">
              {normalizedCandidates.map((u) => {
                const isSelected = selectedSet.has(u.user_id);
                const isPending = pendingUserIdSet.has(u.user_id);
                const isDisabled =
                  submitting ||
                  selectedCurrentUserIds.length > 0 ||
                  isPending ||
                  (!isSelected && availableSlots <= 0) ||
                  (!isSelected && !singleSelect && selectedUserIds.length >= availableSlots);
                return (
                  <button
                    key={`candidate-${u.user_id}`}
                    type="button"
                    onClick={() => toggleCandidate(u.user_id)}
                    className="flex-shrink-0 text-center disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label={`host-role-candidate-${u.code}`}
                    disabled={isDisabled}
                  >
                    <img
                      src={toAvatarSrc(u)}
                      alt={u.code}
                      className={`w-[56px] h-[56px] rounded-full object-cover ring-2 ${
                        isPending
                          ? "ring-amber-300/90 opacity-70"
                          : isSelected
                          ? "ring-[#7BE0FF]"
                          : "ring-white/80"
                      }`}
                    />
                    <div className="mt-1 text-base font-extrabold leading-none">{u.code}</div>
                    <div className="mt-0.5 text-[11px] text-white/70">
                      {isPending
                        ? t("events.details.host_role_modal.pending_badge")
                        : sourceLabel(u.source)}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </section>

    </>
  );

  const confirmationBodyContent = confirmationCandidate ? (
    <div className="space-y-5">
      <div className="flex flex-col items-center text-center">
        <img
          src={toAvatarSrc(confirmationCandidate)}
          alt={confirmationCandidate.code}
          className="h-[72px] w-[72px] rounded-full object-cover ring-2 ring-[#7BE0FF]"
        />
        <div className="mt-2 text-xl font-extrabold leading-none text-white">
          {confirmationCandidate.code}
        </div>
        <div className="mt-1 text-xs font-medium text-white/65">
          {sourceLabel(confirmationCandidate.source)}
        </div>
      </div>
      {confirmationTitle ? (
        <h3 className="text-center text-lg font-semibold leading-7 text-white">
          {confirmationTitle}
        </h3>
      ) : null}
      {typeof renderConfirmationContent === "function"
        ? renderConfirmationContent(confirmationCandidate)
        : renderConfirmationContent}
    </div>
  ) : null;

  const bodyContent = confirmationBodyContent || selectionBodyContent;
  const activeSubtitle = confirmationCandidate
    ? confirmationSubtitle
    : subtitle;
  const handleHeaderBack = confirmationCandidate
    ? () => setConfirmationUserId(null)
    : onBack;

  if (embedded) {
    return (
      <>
        {activeSubtitle ? (
          <p className="mb-5 text-center text-[15px] font-medium leading-7 text-white/88">
            {activeSubtitle}
          </p>
        ) : null}
        {bodyContent}
        {footerContent ? <div className="mt-5">{footerContent}</div> : null}
      </>
    );
  }

  return (
    <AppModalShell
      open={visible}
      title={title}
      description={activeSubtitle}
      onClose={onClose}
      closeAriaLabel={t("events.details.host_role_modal.close")}
      zIndexClassName="z-[1200]"
      maxWidthClassName="max-w-[680px]"
      descriptionClassName="text-center"
      leftAction={
        typeof handleHeaderBack === "function" ? (
          <EventToolsBackButton
            onClick={handleHeaderBack}
            label={t("events.details.more_actions.back")}
            disabled={submitting}
          />
        ) : null
      }
      footer={footerContent}
    >
      {bodyContent}
    </AppModalShell>
  );
};

export default HostRoleRequestModal;
